import { Link } from 'react-router-dom';

import {
  useCrawlTick,
  useCrawlerStatus,
  useFeedRuns,
  useFeedSync,
  usePacing,
  useQueueAttempts,
  useQueueFailures,
  useQueueOverview,
  useSummary,
} from '../api/queries';
import { DIVERGE, SERIES, STATUS } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, relative, truncate, when } from '../utils/format';

export function CrawlerPage() {
  const status = useCrawlerStatus();
  const runs = useFeedRuns(10);
  const summary = useSummary();
  const tick = useCrawlTick();
  const sync = useFeedSync();
  const queue = useQueueOverview(20);
  const pacing = usePacing();
  const failures = useQueueFailures(10);
  const attempts = useQueueAttempts();

  const blocked = status.data?.blockedUntil ? new Date(status.data.blockedUntil) > new Date() : false;
  const budgetUsed = status.data ? (status.data.requestsToday / status.data.maxRequestsPerDay) * 100 : 0;
  const queueTotal = status.data
    ? status.data.pending + status.data.running + status.data.done + status.data.failed
    : 0;

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Crawler</h1>
          <p className="page__lede">
            The crawler is deliberately slow: one product at a time, a randomised pause between requests and a daily
            request budget. That is what keeps Heureka answering instead of blocking.
          </p>
        </div>
        <div className="row">
          <button type="button" className="button" disabled={sync.isPending} onClick={() => sync.mutate()}>
            {sync.isPending ? 'Importing…' : 'Import feed now'}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={tick.isPending}
            onClick={() => tick.mutate()}
          >
            {tick.isPending ? 'Starting…' : 'Run one tick'}
          </button>
        </div>
      </header>

      {tick.data && (
        <div className="banner">
          <span aria-hidden="true">◷</span>
          <div>
            <strong>{tick.data.message}</strong>
            <div className="muted" style={{ marginTop: 4 }}>
              {tick.data.started
                ? 'A tick works through several products with a pause between each, so it takes minutes. The queue below and the activity log fill in as it goes.'
                : 'Wait for the running tick to finish; two at once would double the rate Heureka sees.'}
            </div>
          </div>
        </div>
      )}

      {sync.isError && <Failed error={sync.error} />}
      {tick.isError && <Failed error={tick.error} />}

      {blocked && (
        <div className="banner" style={{ borderLeftColor: STATUS.critical }}>
          <span aria-hidden="true">⚠</span>
          <div>
            <strong>Heureka is refusing requests.</strong>
            <div className="muted" style={{ marginTop: 4 }}>
              Crawling is paused until {when(status.data?.blockedUntil)} after {status.data?.consecutiveBlocks}{' '}
              consecutive block(s). Each block widens the cooldown; nothing needs to be done by hand.
            </div>
          </div>
        </div>
      )}

      {status.isError ? (
        <Failed error={status.error} />
      ) : status.isLoading ? (
        <Loading label="Loading crawler state…" />
      ) : (
        status.data && (
          <>
            <div className="grid grid--tiles">
              <StatTile
                label="Waiting in queue"
                value={count(status.data.pending)}
                foot={`of ${count(queueTotal)} queued products`}
                accent={SERIES[0]}
              />
              <StatTile
                label="Scanned this cycle"
                value={count(status.data.done)}
                foot="reset when the rescan window reopens"
                accent={STATUS.good}
              />
              <StatTile
                label="Parked as failed"
                value={count(status.data.failed)}
                foot="retried at the next rescan window"
                accent={status.data.failed > 0 ? STATUS.warning : undefined}
              />
              <StatTile
                label="Requests today"
                value={`${count(status.data.requestsToday)} / ${count(status.data.maxRequestsPerDay)}`}
                foot={`${budgetUsed.toFixed(0)}% of the daily budget`}
                accent={budgetUsed > 90 ? STATUS.critical : DIVERGE.low}
              />
              <StatTile
                label="Last request"
                value={relative(status.data.lastRequestAt)}
                foot={when(status.data.lastRequestAt)}
              />
              <StatTile
                label="Consecutive blocks"
                value={count(status.data.consecutiveBlocks)}
                foot={blocked ? `paused until ${when(status.data.blockedUntil)}` : 'not currently blocked'}
                accent={status.data.consecutiveBlocks > 0 ? STATUS.critical : STATUS.good}
              />
            </div>

            <Card
        title="Can it keep up?"
        note="Required pace against measured pace. Everything on the achieved side is counted from real scans, not derived from the settings."
      >
        {pacing.isLoading ? (
          <Loading />
        ) : pacing.isError ? (
          <Failed error={pacing.error} />
        ) : (
          pacing.data && (
            <div className="grid grid--tiles">
              <StatTile
                label="Cycle fit"
                value={pacing.data.cycleFit === null ? '—' : `${pacing.data.cycleFit.toFixed(2)}×`}
                foot={
                  pacing.data.cycleFit === null
                    ? 'no rescan window set'
                    : pacing.data.cycleFit >= 1
                      ? 'keeping up with the catalogue'
                      : 'below 1 means the catalogue cannot stay fresh'
                }
                accent={
                  pacing.data.cycleFit === null
                    ? undefined
                    : pacing.data.cycleFit >= 1
                      ? STATUS.good
                      : pacing.data.cycleFit >= 0.75
                        ? STATUS.warning
                        : STATUS.critical
                }
              />
              <StatTile
                label="Needed per day"
                value={count(Math.ceil(pacing.data.requiredPerDay))}
                foot={`${count(pacing.data.activeProducts)} products ÷ ${Math.round(pacing.data.rescanAfterHours / 24)} day cycle`}
              />
              <StatTile
                label="Scanned, last 24 h"
                value={count(pacing.data.achievedLast24h)}
                foot={`${count(Math.round(pacing.data.achievedPerDayLast7d))}/day over the last week`}
                accent={SERIES[0]}
              />
              <StatTile
                label="Requests today"
                value={`${count(pacing.data.requestsToday)} / ${count(pacing.data.dailyRequestCap)}`}
                foot={
                  pacing.data.budgetExhaustedAt
                    ? `budget runs out ${when(pacing.data.budgetExhaustedAt)}`
                    : 'the cap will not be reached at this rate'
                }
                accent={SERIES[3]}
              />
              <StatTile
                label="Requests per scan"
                value={pacing.data.requestsPerScan === null ? '—' : pacing.data.requestsPerScan.toFixed(2)}
                foot="measured, not assumed"
              />
              <StatTile
                label="Blocks in a row"
                value={count(pacing.data.consecutiveBlocks)}
                foot={
                  pacing.data.blockedUntil && new Date(pacing.data.blockedUntil) > new Date()
                    ? `cooling down until ${when(pacing.data.blockedUntil)}`
                    : `last request ${relative(pacing.data.lastRequestAt)}`
                }
                accent={pacing.data.consecutiveBlocks > 0 ? STATUS.warning : STATUS.good}
              />
            </div>
          )
        )}
      </Card>

      <Card
        title="Up next"
        note="In the order the crawler will actually take them: priority first, then whichever became due earliest"
      >
        {queue.isLoading ? (
          <Loading />
        ) : queue.isError ? (
          <Failed error={queue.error} />
        ) : (
          queue.data && (
            <>
              <div className="grid grid--tiles">
                <StatTile label="Due now" value={count(queue.data.dueNow)} foot="the crawler could take these" />
                <StatTile label="Waiting on a backoff" value={count(queue.data.waiting)} foot="pending, not yet eligible" />
                <StatTile
                  label="Outside the cycle"
                  value={count(queue.data.overdue)}
                  foot={
                    /* The age is only an OVERDUE age when something is overdue;
                       otherwise it is just the oldest scan, and reporting it
                       beside a zero reads as a contradiction. */
                    queue.data.overdue > 0
                      ? `stalest is ${Math.round(queue.data.oldestOverdueHours / 24)} days old`
                      : 'everything inside its window'
                  }
                  accent={queue.data.overdue > 0 ? STATUS.warning : STATUS.good}
                />
                <StatTile label="Never scanned" value={count(queue.data.neverScanned)} />
              </div>

              {queue.data.running.length > 0 && (
                <div className="banner" style={{ marginTop: 'var(--space-4)' }}>
                  <span aria-hidden="true">◷</span>
                  <div>
                    {queue.data.running.map((task) => (
                      <div key={task.shopProductId}>
                        <strong>{task.name}</strong> — running {Math.round(task.runningForSeconds / 60)} min
                        {task.stale && (
                          <>
                            {' '}
                            <Badge label="stale, will be released" color={DIVERGE.high} />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {queue.data.next.length === 0 ? (
                <div className="state" style={{ marginTop: 'var(--space-4)' }}>
                  Nothing is due. Every product is inside its rescan window.
                </div>
              ) : (
                <div className="table-wrap" style={{ marginTop: 'var(--space-4)' }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        <th>Product</th>
                        <th className="num">Priority</th>
                        <th className="num">Attempts</th>
                        <th>Last scanned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.data.next.map((entry) => (
                        <tr key={entry.shopProductId}>
                          <td className="num secondary">{entry.position}</td>
                          <td>
                            <Link to={`/products/${entry.shopProductId}`}>{truncate(entry.name, 58)}</Link>
                          </td>
                          <td className="num secondary">{entry.priority}</td>
                          <td className="num">
                            {entry.attempts > 0 ? <Badge label={String(entry.attempts)} color={STATUS.warning} /> : '—'}
                          </td>
                          <td className="secondary">
                            {entry.hoursSinceScan === null
                              ? 'never'
                              : `${Math.round(entry.hoursSinceScan / 24)} d ago`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )
        )}
      </Card>

      <div className="grid grid--halves">
        <Card title="Why tasks failed" note="Failed tasks grouped by the error they stored">
          {failures.isLoading ? (
            <Loading />
          ) : failures.isError ? (
            <Failed error={failures.error} />
          ) : (failures.data?.length ?? 0) === 0 ? (
            <div className="state">Nothing has failed.</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">Tasks</th>
                    <th>Reason</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.data?.map((failure) => (
                    <tr key={failure.reason}>
                      <td className="num">{count(failure.tasks)}</td>
                      <td>{failure.reason}</td>
                      <td className="secondary">{relative(failure.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Retries left"
          note="A task that fails its last attempt is not retried until the whole rescan interval has passed"
        >
          {attempts.isLoading ? (
            <Loading />
          ) : attempts.isError ? (
            <Failed error={attempts.error} />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">Attempts used</th>
                    <th className="num">Tasks</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {attempts.data?.map((bucket) => (
                    <tr key={bucket.attempts}>
                      <td className="num">{bucket.attempts}</td>
                      <td className="num">{count(bucket.tasks)}</td>
                      <td>
                        {bucket.lastChance && <Badge label="last chance" color={DIVERGE.high} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="Coverage" note="How much of the catalogue has a usable Heureka comparison">
              {summary.data ? (
                <div className="grid grid--tiles">
                  <StatTile label="Active products" value={count(summary.data.activeProducts)} />
                  <StatTile
                    label="With an EAN"
                    value={count(summary.data.productsWithEan)}
                    foot="the only identifier both sides share"
                  />
                  <StatTile label="Matched" value={count(summary.data.matchedProducts)} accent={STATUS.good} />
                  <StatTile
                    label="Not listed on Heureka"
                    value={count(summary.data.notFoundProducts)}
                    foot="searched, nothing convincing found"
                  />
                  <StatTile
                    label="Ambiguous"
                    value={count(summary.data.ambiguousProducts)}
                    foot="two candidates scored too close"
                    accent={STATUS.warning}
                  />
                  <StatTile label="Never scanned" value={count(summary.data.neverScannedProducts)} />
                </div>
              ) : (
                <Loading />
              )}
            </Card>
          </>
        )
      )}

      <Card title="Feed imports" note="Newest first">
        {runs.isError ? (
          <Failed error={runs.error} />
        ) : runs.isLoading ? (
          <Loading />
        ) : (runs.data?.length ?? 0) === 0 ? (
          <div className="state">No feed import has run yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th className="num">Items</th>
                  <th className="num">Created</th>
                  <th className="num">Updated</th>
                  <th className="num">Deactivated</th>
                  <th className="num">Price changes</th>
                  <th className="num">No EAN</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {runs.data?.map((run) => (
                  <tr key={run.id}>
                    <td className="secondary">{when(run.startedAt)}</td>
                    <td style={{ color: run.status === 'success' ? STATUS.good : STATUS.critical }}>{run.status}</td>
                    <td className="num">{count(run.itemsTotal)}</td>
                    <td className="num">{count(run.itemsCreated)}</td>
                    <td className="num">{count(run.itemsUpdated)}</td>
                    <td className="num">{count(run.itemsDeactivated)}</td>
                    <td className="num">{count(run.priceChanges)}</td>
                    <td className="num">{count(run.itemsWithoutEan)}</td>
                    <td className="secondary" style={{ whiteSpace: 'normal', maxWidth: 280 }}>
                      {run.error ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
